import React, { memo, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles } from '@Sage/hoi-poi-ui';

import { SYNC_COMPLETE_HOLD_MS } from '../../constants';
import { syncing as syncingLottie } from '../../lottieFiles';
import CenteredActionScreen from '../CenteredActionScreen';
import SyncStepper, { STEP_STATUS } from '../SyncStepper';

import styles from './styles.js';

const useStyles = createUseStyles(styles, 'SyncInProgress');

const SYNC_STEP_LITERAL_KEYS = [
    'label_s200uki_sync_step_select',
    'label_s200uki_sync_step_preparing',
    'label_s200uki_sync_step_syncing',
    'label_s200uki_sync_step_completed',
];

const getStepStatus = (stepIndex, activeStep, isSuccess) => {
    const stepNumber = stepIndex + 1;

    if (stepNumber < activeStep || (stepNumber === 4 && isSuccess)) {
        return STEP_STATUS.COMPLETED;
    }

    if (stepNumber === activeStep && !(stepNumber === 4 && isSuccess)) {
        return STEP_STATUS.ACTIVE;
    }

    return STEP_STATUS.PENDING;
};

const SyncInProgress = ({
    activeStep = 2,
    completeHoldMs = SYNC_COMPLETE_HOLD_MS,
    isSuccess = false,
    onComplete,
    t,
}) => {
    const classes = useStyles();
    const [hasTriggeredComplete, setHasTriggeredComplete] = useState(false);

    const steps = useMemo(
        () =>
            SYNC_STEP_LITERAL_KEYS.map((key, index) => ({
                label: t(key),
                status: getStepStatus(index, activeStep, isSuccess),
            })),
        [activeStep, isSuccess, t],
    );

    useEffect(() => {
        if (!isSuccess) {
            setHasTriggeredComplete(false);
            return undefined;
        }

        if (hasTriggeredComplete) {
            return undefined;
        }

        const timer = setTimeout(() => {
            setHasTriggeredComplete(true);
            onComplete?.();
        }, completeHoldMs);

        return () => clearTimeout(timer);
    }, [completeHoldMs, hasTriggeredComplete, isSuccess, onComplete]);

    return (
        <CenteredActionScreen
            title={
                isSuccess
                    ? t('label_s200uki_sync_complete_title')
                    : t('label_s200uki_sync_progress_title')
            }
            body={
                isSuccess
                    ? t('label_s200uki_sync_complete_body')
                    : t('label_s200uki_sync_progress_body')
            }
            illustration={syncingLottie}
            footer={<SyncStepper className={classes.syncSteps} steps={steps} />}
        />
    );
};

SyncInProgress.propTypes = {
    activeStep: PropTypes.oneOf([2, 3, 4]),
    completeHoldMs: PropTypes.number,
    isSuccess: PropTypes.bool,
    onComplete: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
};

SyncInProgress.displayName = 'SyncInProgress';

export default memo(SyncInProgress);
